import { all, Resolved, task, timeout } from 'ember-concurrency';
import { UploadFile } from 'ember-file-upload';
import moment from 'moment-timezone';
// @ts-ignore
import ActionCell from 'volta/components/cell/action-cell';
// @ts-ignore
import FormatCell from 'volta/components/cell/format-cell';
// @ts-ignore
import IconCell from 'volta/components/cell/icon-cell';
// @ts-ignore
import TagsCell from 'volta/components/cell/tags-cell';
import SelectFilter from 'volta/components/filters/select-filter';
// @ts-ignore
import TagsFilter from 'volta/components/filters/tags-filter';
import ResourceController from 'volta/controllers/resource-controller';
import { IJobResult, TImportTableName } from 'volta/models/types/data-import';
import { getDuration } from 'volta/utils/date-utils';
import { defaultErrorHandler } from 'volta/utils/error-utils';
import {
  offsetFromPage,
  pageLimit,
  serializeFilters
} from 'volta/utils/filters-utils';

import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';

import type IntlService from 'ember-intl/services/intl';
import type SessionService from 'ember-simple-auth/services/session';
import type StoreService from 'volta/services/store';
import type EventStreamService from 'volta/services/event-stream';
import type BvFlashService from 'volta/services/bv-flash';
import type ImportHisto from 'volta/models/import-histo';
import type {
  IDataImportedSuccessfully,
  IDataImportFailed,
  IPersistedImportFile
} from 'volta/models/types/data-import';
const ImportTableName: TImportTableName[] = [
  'part_types',
  'warehouses',
  'warehouse_calendar',
  'parts',
  'unit_conversions',
  'suppliers',
  'supplier_calendar',
  'workshops',
  'workshop_calendar',
  'workshop_capacity_exceptions',
  'inventories',
  'skus_suppliers',
  'sku_capacity_settings',
  'boms',
  'single_level_boms',
  'sku_locations',
  'orders',
  'transactions',
  'inventories_forecast'
];

interface IUploadedState {
  tableName?: TImportTableName;
  error?: string | string[];
  file?: UploadFile;
  importHistoId?: string;
}

type FilesUploadState = Record<string, IUploadedState | undefined>;

export const ImportsTableColumns = (intl: IntlService, store: StoreService) => [
  {
    valuePath: 'isImportLoading',
    width: 20,
    isSortable: false,
    cellComponent: IconCell,
    format: (model: ImportHisto) => {
      const isLoading = model.isImportLoading;
      const isError = model.isImportError;
      const glyph = `${isError ? 'alert-circle' : isLoading ? 'sync' : 'checkmark-circle'}`;
      const color = `${isError ? 'red' : isLoading ? undefined : 'green'}`;
      const tooltip = isError
        ? model.errorMsg
        : isLoading
        ? intl.t('import.inProgress')
        : undefined;
      return {
        visible: true,
        glyph,
        classNames: isLoading ? 'u-animation-spin' : 'u-animation-bounce-in',
        color,
        tooltip
      };
    }
  },
  {
    columnName: intl.t('table'),
    sortKey: 'tblName',
    valuePath: 'tblName',
    width: 120,
    minWidth: 50
  },
  {
    columnName: intl.t('start'),
    sortKey: 'startedAt',
    valuePath: 'startedAt',
    width: 225,
    minWidth: 50,
    cellComponent: FormatCell,
    format: (model: ImportHisto) => {
      let startedAt = moment(model.startedAt).format('L HH:mm:ss');
      let endedAt = moment(model.endedAt).format('L HH:mm:ss');
      return {
        dataType: 'text',
        value: `${startedAt} (${moment(model.startedAt).from(moment())})`,
        tooltipContent: [
          {
            text: startedAt,
            subtext: intl.t('import.startedAt')
          },
          !model.isLoading
            ? {
                text: endedAt,
                subtext: intl.t('import.endedAt')
              }
            : { text: intl.t('import.didNotEnd') }
        ]
      };
    }
  },
  {
    columnName: intl.t('duration'),
    valuePath: 'endedAt',
    isSortable: false,
    width: 75,
    minWidth: 50,
    format: (model: ImportHisto) => {
      return getDuration(model.startedAt, model.endedAt ?? moment());
    }
  },
  {
    columnName: intl.t('total'),
    valuePath: 'result.totalRows',
    textAlign: 'right',
    isSortable: false,
    classNames: 'u-text-mono',
    width: 60,
    minWidth: 50
  },
  {
    columnName: intl.t('new'),
    valuePath: 'result.newRows',
    classNames: 'u-text-mono',
    textAlign: 'right',
    isSortable: false,
    width: 60,
    minWidth: 50
  },
  {
    columnName: intl.t('updated'),
    valuePath: 'result.updatedRows',
    classNames: 'u-text-mono',
    textAlign: 'right',
    isSortable: false,
    width: 60,
    minWidth: 50
  },
  {
    columnName: intl.t('deleted'),
    valuePath: 'result.deletedRows',
    classNames: 'u-text-mono',
    textAlign: 'right',
    isSortable: false,
    width: 60,
    minWidth: 50
  },
  {
    columnName: intl.t('errorsCount'),
    valuePath: 'result.errorRows',
    textAlign: 'right',
    isSortable: false,
    width: 60,
    minWidth: 50,
    cellComponent: FormatCell,
    format: (model: ImportHisto) => {
      let value = parseInt((model.result?.errorRows ?? '0') as string);
      return {
        value: value,
        dataType: 'number',
        cellClasses: value > 0 ? 'u-color-danger' : '',
        tooltip: value > 0 ? model.errorsAsText : undefined
      };
    }
  },
  {
    columnName: intl.t('tags'),
    valuePath: 'tags',
    isSortable: false,
    width: 100,
    minWidth: 50,
    cellComponent: TagsCell
  },
  {
    columnName: '',
    valuePath: 'source-file',
    width: 20,
    isReorderable: false,
    isResizable: false,
    isSortable: false,
    cellComponent: ActionCell,
    format: (model: ImportHisto) => {
      return {
        model: model.sourceFileURL,
        visible: model.sourceFile?.contentLength,
        action: async (url?: string) => (url ? store.downloadFile(url) : undefined),
        icon: 'file-earmark-spreadsheet-fill',
        size: 'xs',
        title: model.sourceFileURL,
        tooltip: intl.t('import.downloadSourceFile')
      };
    }
  },
  {
    columnName: '',
    valuePath: 'error-file',
    width: 20,
    isReorderable: false,
    isResizable: false,
    isSortable: false,
    cellComponent: ActionCell,
    format: (model: ImportHisto) => {
      return {
        model: model.errorsFileURL,
        visible: model.errorsFile?.contentLength,
        action: async (url?: string) => (url ? store.downloadFile(url) : undefined),
        icon: 'file-earmark-spreadsheet-fill',
        size: 'xs',
        color: 'danger',
        title: model.errorsFileURL,
        tooltip: intl.t('import.downloadErrorsFile')
      };
    }
  }
];

export default class SettingsImportController extends ResourceController {
  @service intl!: IntlService;
  @service session!: SessionService;
  @service store!: StoreService;
  @service eventStream!: EventStreamService;
  @service bvFlash!: BvFlashService;

  @tracked sort = '-startedAt';
  @tracked uploadedState: FilesUploadState = {};

  get uploadStateErrors() {
    return Object.entries(this.uploadedState).reduce((acc, [fileName, state]) => {
      if (state?.error) {
        acc[fileName] = state?.error;
      }

      return acc;
    }, {} as Record<string, string | string[] | undefined>);
  }

  get filterDefinitions(): TFilterDefinition[] {
    return [
      {
        queryParam: 'tblName',
        apiParam: 'tblName',
        comparators: ['in'],
        isPrimary: true,
        component: SelectFilter,
        possibleValues: ImportTableName.map((key) => {
          return { key: key, value: key };
        })
      },
      {
        queryParam: 'tags',
        apiParam: 'tags',
        comparators: ['any'],
        component: TagsFilter,
        isPrimary: true,
        entityTypes: ['importHistos']
      }
    ];
  }

  get tableColumns() {
    return ImportsTableColumns(this.intl, this.store);
  }

  // Lifecycle
  // ~~~~~

  // Helper Functions
  // ~~~~~

  // Called in the setupController hook of the route
  setup() {
    this.eventStream.addHandler(
      'dataImport',
      ['DataImportedSuccessfully'],
      this._onDataImportedSuccessfully,
      this
    );
    this.eventStream.addHandler('dataImport', ['DataImportFailed'], this._onDataImportFailed, this);
  }

  /**
   * Called in the resetController hook of the route, when exiting
   */
  teardown() {
    this.eventStream.removeHandler(this);
    this.uploadedState = {};
  }

  @action
  handleFileAdd(file: UploadFile) {
    this.uploadFileTask.perform(file);
  }

  validateFile(file: File) {
    const extension = file.name.split('.').lastObject;
    return extension?.toLowerCase() === 'csv' && file.size <= 500 * 1024 * 1024;
  }

  fileURL(file: IPersistedImportFile) {
    return `importHistos/files/${file.bucket}/${file.key}`;
  }

  uploadFileTask = task({ maxConcurrency: 1, enqueue: true }, async (file: UploadFile) => {
    const tableName = this.extractName(file);
    let state: IUploadedState = {
      error: undefined,
      file
    };
    if (tableName) {
      state.tableName = tableName;

      try {
        state.tableName = tableName;
        this.uploadedState = { ...this.uploadedState, [file.name]: state };
        const fileContent: any = (await file.readAsBinaryString()) as unknown;
        const data = { file: fileContent };

        const [uploadResult, _]: [Resolved<IJobResult>, Resolved<void>] = await all([
          this.store.uploadFile(`jobs/import/async/csv/form/${tableName}`, file, {
            data
          }),
          timeout(500)
        ]);

        if (uploadResult?.importId) {
          state.importHistoId = uploadResult.importId;
          this.uploadedState = { ...this.uploadedState, [file.name]: state };
        }

        return uploadResult;
      } catch (error) {
        state.error = this.intl.t('errors.uploadFailure');
        this.uploadedState = { ...this.uploadedState, [file.name]: state };
        defaultErrorHandler(error);
        throw error;
      }
    } else {
      state.error = this.intl.t('settingsPage.import.unknownTableName');
      this.uploadedState = { ...this.uploadedState, [file.name]: state };
    }
    return undefined;
  });

  extractName(file: UploadFile): TImportTableName | undefined {
    let { name } = file;
    let normalizeKey = name.replace('-', '_').replace('\\.', '_').replace(' ', '_').toLowerCase();
    return ImportTableName.reduce((prev, tableName: TImportTableName) => {
      if (normalizeKey.indexOf(tableName) > -1) {
        return (prev && prev.length < tableName.length) || !prev ? tableName : prev;
      } else {
        return prev;
      }
    }, undefined as unknown as TImportTableName);
  }

  _onDataImportedSuccessfully(payload: IDataImportedSuccessfully) {
    if (this.isDestroying || this.isDestroyed) return;

    const maybeState = Object.entries(this.uploadedState).find(
      ([_, st]) => st?.importHistoId === payload.importHistoId
    );

    // We only display the success message for the latest manually uploaded file
    // otherwise for real-time scheduled imports there would be flash messages all over
    if (maybeState) {
      const [fileName, _] = maybeState;

      this.uploadedState = { ...this.uploadedState, [fileName]: undefined };
      const pageSize = Number(this.queryParamsValues.pageSize ?? '25');
      this.resourceCollectionTask.perform({
        filter: serializeFilters(
          this.queryParamsValues as Record<string, string>,
          this.filterDefinitions
        ),
        page: {
          offset: offsetFromPage(Number(this.queryParamsValues.page ?? '1'), pageLimit(pageSize)),
          limit: pageSize
        },
        sort: this.sort
      });
      this.bvFlash.success(
        this.intl.t('settingsPage.import.importSuccess', {
          tableName: payload.tableName,
          total: payload.result?.totalRows,
          errors: payload.result?.errorRows
        })
      );
    }
  }

  _onDataImportFailed(payload: IDataImportFailed) {
    if (this.isDestroying || this.isDestroyed) return;

    const maybeState = Object.entries(this.uploadedState).find(
      ([_, st]) => st?.importHistoId === payload.importHistoId
    );

    if (maybeState) {
      const [fileName, _] = maybeState;
      const state = this.uploadedState[fileName]!;
      state.error = payload.error?.message;
      this.uploadedState = { ...this.uploadedState, [fileName]: state };
    }
  }
}
